import * as React from 'react';
import { cn } from '@/utils/cn';
import { getFullImageUrl } from '@/utils/url';

/** Props for `Avatar`. Renders `videoSrc` (loop video) when provided, else `src` image, else `initials` placeholder. */
export interface AvatarProps {
  src?: string | null;
  videoSrc?: string | null;
  alt?: string;
  initials?: string;
  size?: number;
  className?: string;
  textClassName?: string;
}

/** Round avatar with image / video / initials fallback chain. */
export const Avatar: React.FC<AvatarProps> = ({
  src,
  videoSrc,
  alt = 'Avatar',
  initials,
  size = 48,
  className,
  textClassName,
}) => {
  const dimension = `${size}px`;

  const imageUrl = React.useMemo(() => getFullImageUrl(src ?? undefined), [src]);
  const videoUrl = React.useMemo(() => getFullImageUrl(videoSrc ?? undefined), [videoSrc]);

  return (
    <div
      className={cn(
        'flex items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-100',
        className
      )}
      style={{ width: dimension, height: dimension }}
    >
      {videoUrl ? (
        <video src={videoUrl} className='size-full object-cover' loop muted autoPlay playsInline />
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt={alt}
          className='size-full object-cover'
          loading='lazy'
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : (
        <span className={cn('font-medium text-gray-600', textClassName)}>{initials}</span>
      )}
    </div>
  );
};

Avatar.displayName = 'Avatar';
